import toastr from 'react-hot-toast';
import { useInfiniteQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import {
  generateFilter,
  generatePaginationQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import type { SparkRating, SparkRatingResponse } from 'types/Spark/SparkRating';

const getSparkRatings = (pageParam: number, sparkID: string | undefined) => {
  if (!sparkID) {
    Promise.reject('spark ID can not be null');
    return;
  }

  const pageSize = pageParam === 1 ? 5 : 10;

  const queryArray = [
    generateFilter('sparkID', sparkID),
    generatePaginationQuery({
      pageNumber: pageParam,
      pageSize,
    }),
  ];

  const url = generateURL(SPARK_RESOURCE_TYPES.SPARK_RATINGS, queryArray);
  return fetchWrapper<SparkRatingResponse>({
    url: url,
    method: 'GET',
  });
};

export const useSparkRatingInfiniteQuery = (sparkID: string | undefined) => {
  const queryResult = useInfiniteQuery(
    ['ratings paginated'],
    ({ pageParam = 1 }) => getSparkRatings(pageParam, sparkID),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.data?.length ? allPages?.length + 1 : undefined;
      },
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Sparks: ${error.message}`);
        }
      },
      enabled: !!sparkID,
      ...CACHE_AND_STALE_TIME,
    }
  );
  const { data: ratingResponse, ...restQueryResult } = queryResult;
  const sparkRatings = ratingResponse?.pages.reduce<SparkRating[]>(
    (acc, page) => {
      if (!page?.data) {
        return acc;
      }
      return [...acc, ...(page.data || [])];
    },
    []
  );

  return {
    sparkRatings,
    ...restQueryResult,
  };
};
